import alt from 'components/Dispatcher';
import {List, Map} from 'immutable';
import {associateBy} from 'utils/associateBy';
import CoachingReviewType, {CoachingReviewTypeId} from 'records/CoachingReviewType';
import CoachingMeetingCategory from 'records/CoachingMeetingCategory';
import {ActionsBase, DispatchAll, Store} from '@store/@types/alt';
import generateAsyncActions from 'utils/generateAsyncActions';
import CoachingReviewTypeSource from 'sources/coaching/CoachingReviewTypeSource';

interface AsyncActions {
  fetchAllReviewTypes: (dirty: boolean) => Promise<CoachingReviewType[]>;
  fetchReviewTypeById: (id: CoachingReviewTypeId) => Promise<CoachingReviewType>;
  upsertReviewType: (type: CoachingReviewType) => Promise<void>;
  deleteReviewType: (id: CoachingReviewTypeId) => Promise<void>;
  fetchMeetingCategories: (dirty?: boolean) => Promise<CoachingMeetingCategory[]>;
}

interface SyncActions {}

/**
 * TODO Resolve the circular dependencies which require these to share a file.
 */
class CoachingReviewTypeActions_unconnected
  extends ActionsBase<AsyncActions, SyncActions>()
  implements DispatchAll<AsyncActions>
{
  constructor() {
    super();
    this.generateActions(
      ...generateAsyncActions(
        'fetchAllReviewTypes',
        'fetchReviewTypeById',
        'upsertReviewType',
        'deleteReviewType',
        'fetchMeetingCategories'
      )
    );
  }

  fetchAllReviewTypes(dirty = false) {
    return dispatch => {
      dispatch(dirty);

      const {typesLoaded, typesError} = CoachingReviewTypeStore.getState();

      if (!dirty && typesLoaded && !typesError) {
        return;
      }

      return CoachingReviewTypeSource.fetchAll().then(
        this.fetchAllReviewTypesSuccess,
        this.fetchAllReviewTypesError
      );
    };
  }

  fetchReviewTypeById(id) {
    return dispatch => {
      dispatch(id);
      return CoachingReviewTypeSource.fetchById(id).then(
        this.fetchReviewTypeByIdSuccess,
        this.fetchReviewTypeByIdError
      );
    };
  }

  upsertReviewType(coachingReviewType) {
    return dispatch => {
      dispatch(coachingReviewType);
      return CoachingReviewTypeSource.upsert(coachingReviewType).then(
        this.upsertReviewTypeSuccess,
        this.upsertReviewTypeError
      );
    };
  }

  deleteReviewType(id) {
    return dispatch => {
      dispatch(id);
      return CoachingReviewTypeSource.delete(id).then(
        this.deleteReviewTypeSuccess,
        this.deleteReviewTypeError
      );
    };
  }

  /**
   * Meeting categories are completely static right now.
   *
   * If we've loaded them already, this is a no-op
   *
   */
  fetchMeetingCategories(dirty = false) {
    return dispatch => {
      dispatch();

      const {meetingCategories, meetingCategoriesLoaded, meetingCategoriesError} =
        CoachingReviewTypeStore.getState();

      if (
        !dirty &&
        meetingCategoriesLoaded &&
        meetingCategories &&
        meetingCategories.size > 0 &&
        !meetingCategoriesError
      ) {
        return;
      }

      return CoachingReviewTypeSource.fetchMeetingCategories().then(
        this.fetchMeetingCategoriesSuccess,
        this.fetchMeetingCategoriesError
      );
    };
  }
}
export const CoachingReviewTypeActions = alt.createActions<AsyncActions, SyncActions>(
  CoachingReviewTypeActions_unconnected
);

/**
 * Get the load state and values of review types and meeting categories
 *
 * If you use this - make sure you have a loading strategy (quite possibly already loaded for you via
 * StatsPage2 `ensureReviewMetadata`), that you respect the returned loadState, and that you've got
 * the CoachingReviewTypeStore in getStores() somewhere so you subscribe to updates.
 *
 * @return [loadState, List<CoachingReviewType>] - load state and type info (with embedded category)
 */
export const typesAndCategories = CoachingReviewTypeStore => {
  const {types, typesLoaded, meetingCategories, meetingCategoriesLoaded} =
    CoachingReviewTypeStore.getState();

  if (!(types && typesLoaded && meetingCategories && meetingCategoriesLoaded)) {
    return [false, List()];
  }

  return [
    true,
    types.map(t => t.set('meetingCategory', meetingCategories.get(t.meetingCategoryId))),
  ];
};

export const categories = CoachingReviewTypeStore => {
  const {meetingCategories, meetingCategoriesLoaded} = CoachingReviewTypeStore.getState();

  if (!(meetingCategories && meetingCategoriesLoaded)) {
    return [false, List()];
  }

  return [true, meetingCategories];
};

interface CoachingReviewTypeStoreState {
  defaultType: undefined;
  types: Map<number, CoachingReviewType>;
  typesLoading: boolean;
  typesLoaded: boolean;
  typesError: boolean;
  meetingCategories?: Map<string, CoachingMeetingCategory>;
  meetingCategoriesLoaded: boolean;
  meetingCategoriesError: boolean;
}

/**
 * Note: this is a store that is used both for Coaching Review Type CRUD operations
 * as well as surfacing the review type/meeting categories for dashboard reports.
 */
class CoachingReviewTypeStore_unconnected extends Store<CoachingReviewTypeStoreState> {
  constructor() {
    super();
    this.state = {
      defaultType: undefined,
      types: Map(),
      typesLoading: false,
      typesLoaded: false,
      typesError: false,
      meetingCategories: undefined,
      meetingCategoriesLoaded: false,
      meetingCategoriesError: false,
    };
    this.bindActions(CoachingReviewTypeActions);
  }

  fetchAllReviewTypes(dirty) {
    if (!dirty) {
      return;
    }

    this.setState({
      typesLoading: true,
      typesError: false,
    });
  }

  fetchAllReviewTypesSuccess(types) {
    this.setState({
      defaultType: types.find(t => t.isDefault),
      types: associateBy(types, t => t.id!),
      typesLoading: false,
      typesLoaded: true,
      typesError: false,
    });
  }

  fetchAllReviewTypesError() {
    this.setState({
      typesLoading: false,
      typesLoaded: false,
      typesError: true,
    });
  }

  fetchReviewTypeById() {
    this.setState({
      typesLoading: true,
      typesError: true,
    });
  }

  fetchReviewTypeByIdSuccess(type) {
    this.setState({
      types: this.state.types.set(type.id, type),
      typesLoading: false,
      typesError: false,
    });
  }

  fetchReviewTypeByIdError() {
    this.setState({
      typesLoading: false,
      typesError: true,
    });
  }

  upsertReviewType() {
    this.setState({
      typesLoading: true,
      typesError: true,
    });
  }

  upsertReviewTypeSuccess(type) {
    this.setState({
      types: this.state.types.set(type.id, type),
      typesLoading: false,
      typesError: false,
    });
  }

  upsertReviewTypeError() {
    this.setState({
      typesLoading: false,
      typesError: true,
    });
  }

  deleteReviewType() {
    this.setState({
      typesLoading: true,
      typesError: true,
    });
  }

  deleteReviewTypeSuccess(id) {
    this.setState({
      types: this.state.types.remove(id),
      typesLoading: false,
      typesError: false,
    });
  }

  deleteReviewTypeError() {
    this.setState({
      typesLoading: false,
      typesError: true,
    });
  }

  fetchMeetingCategoriesSuccess(categories) {
    this.setState({
      meetingCategories: Map(categories.map(c => [c.id, c])),
      meetingCategoriesLoaded: true,
      meetingCategoriesError: false,
    });
  }

  fetchMeetingCategoriesError() {
    this.setState({
      meetingCategoriesLoaded: true,
      meetingCategoriesError: true,
    });
  }
}

const CoachingReviewTypeStore = alt.createStore(CoachingReviewTypeStore_unconnected);
export default CoachingReviewTypeStore;
